<template>
  <div class="member-list">
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters">
        <el-form-item label="地区筛选">
          <el-cascader
            v-model="filters.address"
            :options="addressOptions"
            placeholder="请选择地区"
            clearable
            @change="handleFilterChange"
          />
        </el-form-item>
        
        <el-form-item label="关键词">
          <el-input
            v-model="filters.keyword"
            placeholder="搜索会员姓名"
            clearable
            @clear="handleFilterChange"
          >
            <template #append>
              <el-button :icon="Search" @click="handleFilterChange" />
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 会员列表 -->
    <div class="member-grid">
      <el-row :gutter="20">
        <el-col
          v-for="member in members"
          :key="member.id"
          :xs="24"
          :sm="12"
          :md="8"
          :lg="6"
        >
          <el-card class="member-card" shadow="hover" @click="goToDetail(member.id)">
            <!-- 会员照片 -->
            <div class="member-photo">
              <el-image
                :src="member.photo || '/static/images/default-avatar.png'"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <el-icon><User /></el-icon>
                  </div>
                </template>
              </el-image>
            </div>
            
            <!-- 会员信息 -->
            <div class="member-info">
              <h3 class="member-name">{{ member.real_name }}</h3>
              
              <div class="member-title">
                {{ member.title || '心理咨询师' }}
              </div>
              
              <div class="member-meta">
                <div class="meta-item">
                  <el-icon><Location /></el-icon>
                  <span>{{ member.address }}</span>
                </div>
                <div class="meta-item">
                  <el-icon><OfficeBuilding /></el-icon>
                  <span>{{ member.company }}</span>
                </div>
              </div>
              
              <el-tag type="success" size="small">注册会员</el-tag>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 空状态 -->
      <el-empty v-if="!loading && members.length === 0" description="暂无会员" />
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="total > 0">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :total="total"
        :page-sizes="[12, 24, 36, 48]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Search, User, Location, OfficeBuilding } from '@element-plus/icons-vue'

export default {
  name: 'MemberList',
  
  components: {
    // eslint-disable-next-line vue/no-unused-components
    Search,
    User,
    Location,
    OfficeBuilding
  },
  
  data() {
    return {
      filters: {
        address: [],
        keyword: ''
      },
      pagination: {
        page: 1,
        pageSize: 12
      },
      loading: false,
      // 地区选项（实际应从API获取）
      addressOptions: [
        {
          value: '广东省',
          label: '广东省',
          children: [
            { value: '深圳市', label: '深圳市' },
            { value: '广州市', label: '广州市' },
            { value: '东莞市', label: '东莞市' }
          ]
        },
        {
          value: '北京市',
          label: '北京市',
          children: [
            { value: '东城区', label: '东城区' },
            { value: '西城区', label: '西城区' }
          ]
        }
      ]
    }
  },
  
  computed: {
    ...mapState('member', ['publicMembers', 'total']),
    
    members() {
      return this.publicMembers
    }
  },
  
  mounted() {
    this.loadMembers()
  },
  
  methods: {
    ...mapActions('member', ['fetchPublicMembers']),
    
    /**
     * 加载会员列表
     */
    async loadMembers() {
      this.loading = true
      
      try {
        await this.fetchPublicMembers({
          address: this.filters.address.join('-'),
          keyword: this.filters.keyword,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
      } catch (error) {
        console.error('加载会员失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 筛选条件变化
     */
    handleFilterChange() {
      this.pagination.page = 1
      this.loadMembers()
    },
    
    /**
     * 页码变化
     */
    handlePageChange(page) {
      this.pagination.page = page
      this.loadMembers()
    },
    
    /**
     * 每页数量变化
     */
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadMembers()
    },
    
    /**
     * 跳转到会员详情
     */
    goToDetail(memberId) {
      this.$router.push(`/member/detail/${memberId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.member-list {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
  
  .filter-card {
    margin-bottom: 20px;
  }
  
  .member-grid {
    min-height: 400px;
    
    .member-card {
      margin-bottom: 20px;
      cursor: pointer;
      transition: transform 0.3s;
      
      &:hover {
        transform: translateY(-5px);
      }
      
      .member-photo {
        width: 100%;
        height: 200px;
        margin-bottom: 15px;
        border-radius: 8px;
        overflow: hidden;
        
        .el-image {
          width: 100%;
          height: 100%;
        }
        
        .image-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background-color: #f5f7fa;
          color: #909399;
          font-size: 60px;
        }
      }
      
      .member-info {
        text-align: center;
        
        .member-name {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 500;
          color: #303133;
        }
        
        .member-title {
          margin-bottom: 12px;
          font-size: 14px;
          color: #606266;
        }
        
        .member-meta {
          margin-bottom: 12px;
          
          .meta-item {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            margin-bottom: 6px;
            font-size: 13px;
            color: #909399;
            
            .el-icon {
              font-size: 14px;
            }
            
            span {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
      }
    }
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 30px;
  }
}

@media (max-width: 768px) {
  .member-list {
    padding: 10px;
  }
}
</style>
